<template>
  <component
    :is="tag"
    class="m-heading"
    :class="{
      [`m-heading--${renderSize}`]: renderSize,
      ['m-heading--hyphenated']: hyphenate,
      'm-heading--margin': bottomMargin,
    }"
  >
    <slot />
  </component>
</template>

<script>
const nativeSizes = ['h1', 'h2', 'h3', 'h4', 'h5']
const sizes = [
  undefined,
  ...nativeSizes,
  // Extra large, for marketing sites
  'display1',
  'display2',
]

/**
 * Heading or title component with semantics detached from display.
 * Choose any tag and any size.
 */
export default {
  name: 'MHeading',
  props: {
    tag: {
      type: String,
      default: 'h1',
    },
    /**
     * Set the display size of the heading.
     * If not set, size is inferred from tag heading level where possible, else renders at h1 size.
     */
    size: {
      type: String,
      default: undefined,
      validator: (prop) => sizes.includes(prop),
    },
    /**
     * Allows the title to hyphenate automatically when words are longer than the element width.
     */
    hyphenate: {
      type: Boolean,
      default: false,
    },
    /**
     * Controls bottom margin to push other elements away.
     */
    bottomMargin: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    renderSize() {
      if (this.size) {
        return this.size
      } else if (nativeSizes.includes(this.tag)) {
        return this.tag
      } else {
        return 'h1'
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@use '../../../sass/framework/mixins/font-size';

.m-heading {
  color: var(--skin-heading-color);
  font-family: var(--typography-headings-font-family);
  font-weight: var(--typography-headings-font-weight);
  hyphens: manual;
  line-height: var(--typography-headings-line-height);
  margin-bottom: 0;
  text-wrap: balance;

  @each $heading in (h1, h2, h3, h4, h5) {
    &--#{$heading} {
      @include font-size.font-size($heading);
    }
  }

  &--display1 {
    @include font-size.font-size(display1);
  }

  &--display2 {
    @include font-size.font-size(display2);
  }

  &--hyphenated {
    hyphens: auto;
    word-wrap: break-word;
  }

  &--margin {
    margin-bottom: var(--spacing-16);
  }
}
</style>
